import { PhoneNumberUtil } from 'google-libphonenumber';

export interface CountryOption {
  name: string;
  dialCode: string;
  countryCode: string;
}

const phoneUtil = PhoneNumberUtil.getInstance();

const getRegionDisplayName = (region: string, language: string): string => {
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(region) ?? region;
  } catch {
    return region;
  }
};

/** ISO region codes supported by libphonenumber, with dial codes and English labels. */
export const COUNTRY_OPTIONS: CountryOption[] = phoneUtil
  .getSupportedRegions()
  .map((region) => ({
    countryCode: region.toLowerCase(),
    dialCode: `+${phoneUtil.getCountryCodeForRegion(region)}`,
    name: getRegionDisplayName(region, 'en'),
  }))
  .sort((a, b) => a.name.localeCompare(b.name, 'en'));

/** Returns a flag emoji for the given ISO 3166-1 alpha-2 country code. */
export const getFlagEmoji = (countryCode: string): string => {
  return countryCode.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
};

/** Builds a localized country-name map keyed by the English label used in {@link COUNTRY_OPTIONS}. */
export const buildCountryLocalization = (language: string): Record<string, string> => {
  return COUNTRY_OPTIONS.reduce<Record<string, string>>((map, { name, countryCode }) => {
    const localized = getRegionDisplayName(countryCode.toUpperCase(), language);
    if (localized !== countryCode.toUpperCase() && localized !== name) {
      map[name] = localized;
    }
    return map;
  }, {});
};

export const DEFAULT_COUNTRY_CODE = 'us';

export const getCountryOptionByCode = (countryCode: string): CountryOption | undefined => {
  return COUNTRY_OPTIONS.find((country) => country.countryCode === countryCode.toLowerCase());
};
